import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { 
  ArrowLeft,
  Smartphone,
  Globe,
  Wallet,
  Shield,
  Check,
  Loader2,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const SMSSettings = ({ user }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [phoneNumber, setPhoneNumber] = useState("");
  const [remittanceAlerts, setRemittanceAlerts] = useState(true);
  const [transactionAlerts, setTransactionAlerts] = useState(true);
  const [securityAlerts, setSecurityAlerts] = useState(true);

  // Fetch current SMS preferences
  const { data: preferences, isLoading } = useQuery({
    queryKey: ["smsPreferences"],
    queryFn: async () => {
      const response = await axios.get(`${API}/user/sms-preferences`);
      return response.data;
    }
  });

  // Fetch SMS history
  const { data: smsHistory } = useQuery({
    queryKey: ["smsHistory"],
    queryFn: async () => {
      const response = await axios.get(`${API}/notifications/sms-history`);
      return response.data;
    }
  });

  // Update form when preferences load
  useEffect(() => {
    if (preferences) {
      setPhoneNumber(preferences.phone_number || "");
      setRemittanceAlerts(preferences.remittance_alerts ?? true);
      setTransactionAlerts(preferences.transaction_alerts ?? true);
      setSecurityAlerts(preferences.security_alerts ?? true);
    }
  }, [preferences]);

  // Save preferences mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/user/sms-preferences`, data);
      return response.data;
    },
    onSuccess: () => {
      toast.success("SMS preferences saved!");
      queryClient.invalidateQueries(["smsPreferences"]);
      queryClient.invalidateQueries(["smsHistory"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to save preferences");
    }
  });

  const handleSave = () => {
    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    saveMutation.mutate({
      phone_number: phoneNumber,
      remittance_alerts: remittanceAlerts,
      transaction_alerts: transactionAlerts,
      security_alerts: securityAlerts
    });
  };

  const messages = smsHistory?.messages || [];

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="sms-settings-page">
      {/* Header */}
      <header className="px-5 pt-4 pb-2 flex items-center gap-4 max-w-lg mx-auto">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/settings")}
          className="text-white hover:bg-white/10"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="font-heading font-semibold text-xl text-white">SMS Alerts</h1>
      </header>

      <main className="px-5 max-w-lg mx-auto space-y-6">
        {/* Phone Number Section */}
        <div className="glass rounded-2xl p-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Smartphone className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="font-medium text-white">Phone Number</h2>
              <p className="text-xs text-muted-foreground">For receiving SMS alerts</p>
            </div>
          </div>
          
          <Input
            type="tel"
            placeholder="+1 (876) 555-0123"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            className="h-12 bg-white/5 border-white/20 text-white placeholder:text-muted-foreground"
            data-testid="phone-input"
          />
          
          {preferences?.verified && (
            <div className="flex items-center gap-2 mt-3 text-emerald-400 text-sm">
              <Check className="w-4 h-4" />
              <span>Verified</span>
            </div>
          )}
        </div>

        {/* Alert Preferences */}
        <div className="glass rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h2 className="font-medium text-white">Alert Types</h2>
            <p className="text-xs text-muted-foreground mt-1">Choose which alerts to receive</p>
          </div>

          {/* Remittance Alerts */}
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Remittance Credits</p>
                <p className="text-xs text-muted-foreground">When you receive money from abroad</p>
              </div>
            </div>
            <Switch
              checked={remittanceAlerts}
              onCheckedChange={setRemittanceAlerts}
              data-testid="remittance-toggle"
            />
          </div>

          {/* Transaction Alerts */}
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Wallet className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Transaction Alerts</p>
                <p className="text-xs text-muted-foreground">For large transactions</p>
              </div>
            </div>
            <Switch
              checked={transactionAlerts}
              onCheckedChange={setTransactionAlerts}
              data-testid="transaction-toggle"
            />
          </div>

          {/* Security Alerts */}
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-white">Security Alerts</p>
                <p className="text-xs text-muted-foreground">Login attempts & suspicious activity</p>
              </div>
            </div>
            <Switch
              checked={securityAlerts}
              onCheckedChange={setSecurityAlerts}
              data-testid="security-toggle"
            />
          </div>
        </div>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saveMutation.isPending || !phoneNumber}
          className="w-full h-12 btn-primary"
          data-testid="save-sms-btn"
        >
          {saveMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Save Preferences"
          )}
        </Button>

        {/* Recent SMS History */}
        {messages.length > 0 && (
          <div>
            <h3 className="text-sm font-medium text-white mb-3">Recent SMS</h3>
            <div className="space-y-2">
              {messages.slice(0, 5).map((msg) => (
                <div
                  key={msg.sms_id}
                  className="p-3 rounded-xl bg-white/5 border border-white/10"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground capitalize">
                        {msg.notification_type?.replace("_", " ")}
                      </span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      msg.status === "delivered" 
                        ? "bg-emerald-500/20 text-emerald-400"
                        : "bg-amber-500/20 text-amber-400"
                    }`}>
                      {msg.status}
                    </span>
                  </div>
                  <p className="text-sm text-white line-clamp-2">{msg.message}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sandbox Notice */}
        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <p className="text-xs text-amber-200 text-center">
            SMS notifications are in sandbox mode. Messages are logged but not actually sent.
          </p>
        </div>
      </main>
    </div>
  );
};

export default SMSSettings;
